import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck, Loader2, X } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ConfirmDialog from './ConfirmDialog';
import { formatMoney } from '../lib/formatters';

/*
 * Üye işlem penceresi (düzenle, bakiye ekle/düş, şifre, affiliate komisyonu,
 * yeni üye). Kullanıcılar listesi ile üye profili aynı pencereyi açar; iki
 * kopya ayrışsaydı profilden verilen bakiye listedekinden başka kurallarla
 * işlenirdi (bkz. useUserActions).
 */

// 2FA kanalının okunur adı: üyenin kaydı ``sms`` / ``email`` / ``totp``
// tutar ve yönetici "kod nereye gidiyor" sorusunu penceredeki rozetten
// yanıtlar. Tanınmayan (ya da bu özellikten önce açılmış, kanalı boş) kayıt
// uydurma bir kanal adı yerine yalnızca "Açık" yazar.
const TWO_FACTOR_CHANNEL_LABELS = {
  sms: 'SMS',
  email: 'E-posta',
  totp: 'Google Authenticator',
};

export const twoFactorChannelLabel = (channel) => (
  TWO_FACTOR_CHANNEL_LABELS[String(channel || '').toLowerCase()] || 'Açık'
);

// Testler modalı tek başına kurar: bütün Users sayfasını ayağa kaldırmak
// kimlik kapısını sınamak için gereksiz bir liste isteği zinciri açardı.
export const UserModal = ({ modal, onClose, onAction, onCreated }) => {
  const { type, user, sign } = modal;
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(100);
  const [wagerMultiplier, setWagerMultiplier] = useState(0);
  const [note, setNote] = useState('');
  const [form, setForm] = useState({ email: user?.email || '', phone: user?.phone || '', first_name: user?.first_name || '', last_name: user?.last_name || '', tc_no: user?.tc_no || '', birth_date: user?.birth_date || '', kyc_verified: !!user?.kyc_verified, is_active: user?.is_active !== false, is_banned: !!user?.is_banned, two_factor_enabled: !!user?.two_factor_enabled });
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', phone: '', first_name: '', last_name: '', balance: 0 });
  const [confirmBalance, setConfirmBalance] = useState(false);
  const [password, setPassword] = useState({ value: '', repeat: '' });
  const [commission, setCommission] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);

  const loadCommission = useCallback(async () => {
    if (!user) return;
    setCommissionLoading(true);
    try {
      const { data } = await client.get(`/admin/affiliate/by-user/${user.id}`);
      const aff = data.affiliate;
      setCommission({
        affiliate: aff,
        can_use_affiliate: data.can_use_affiliate,
        commission_type: aff?.commission_type || 'revshare',
        revshare_percent: aff?.revshare_percent ?? '',
        deposit_percent: aff?.deposit_percent ?? '',
        cpa_amount: aff?.cpa_amount ?? '',
        cpa_min_deposit: aff?.cpa_min_deposit ?? '',
      });
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Affiliate bilgisi alınamadı',
      });
    } finally {
      setCommissionLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    if (type === 'commission') loadCommission();
  }, [type, loadCommission]);

  const doPassword = async () => {
    if (password.value.length < 8) {
      toast({ title: 'Şifre en az 8 karakter olmalı' });
      return;
    }
    if (password.value !== password.repeat) {
      toast({ title: 'Şifreler eşleşmiyor' });
      return;
    }
    setBusy(true);
    try {
      await client.post(`/admin/users/${user.id}/password`, { password: password.value });
      toast({
        title: 'Şifre sıfırlandı',
        description: `${user.username} kullanıcısının açık oturumları kapatıldı.`,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Şifre değiştirilemedi',
      });
    } finally { setBusy(false); }
  };

  const createAffiliate = async () => {
    setBusy(true);
    try {
      await client.post('/admin/affiliate', { user_id: user.id, grant_access: true });
      toast({ title: 'Ortak oluşturuldu', description: `${user.username} için referans kodu üretildi.` });
      await loadCommission();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Ortak oluşturulamadı',
      });
    } finally { setBusy(false); }
  };

  const saveCommission = async () => {
    const kind = commission.commission_type;
    const value = kind === 'cpa' ? commission.cpa_amount
      : kind === 'deposit' ? commission.deposit_percent : commission.revshare_percent;
    if (value === '' || Number.isNaN(Number(value))) {
      toast({ title: 'Eksik bilgi', description: 'Komisyon değerini girin' });
      return;
    }
    setBusy(true);
    try {
      const payload = { commission_type: kind };
      if (kind === 'cpa') {
        payload.cpa_amount = Number(commission.cpa_amount);
        payload.cpa_min_deposit = Number(commission.cpa_min_deposit || 0);
      } else if (kind === 'deposit') {
        payload.deposit_percent = Number(commission.deposit_percent);
      } else {
        payload.revshare_percent = Number(commission.revshare_percent);
      }
      await client.patch(`/admin/affiliate/${commission.affiliate.id}`, payload);
      toast({ title: 'Komisyon tanımlandı' });
      onClose();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.detail || 'Komisyon kaydedilemedi',
      });
    } finally { setBusy(false); }
  };

  // Çevrim şartı yalnızca bakiye eklemede anlamlıdır; düşme işlemine bağlanmaz.
  const wagerFactor = sign > 0 ? Math.max(0, Number(wagerMultiplier) || 0) : 0;
  const wagerRequired = wagerFactor * (Number(amount) || 0);

  const applyBalance = async () => {
    setBusy(true);
    await onAction(user, 'balance', {
      balance_delta: sign * Number(amount),
      wager_multiplier: wagerFactor,
      note,
    });
    setBusy(false);
    setConfirmBalance(false);
  };
  const doBalance = () => {
    if (!Number.isFinite(Number(amount)) || Number(amount) <= 0) {
      toast({ title: 'Geçerli bir tutar girin' });
      return;
    }
    if (!Number.isFinite(Number(wagerMultiplier)) || Number(wagerMultiplier) < 0) {
      toast({ title: 'Çevrim çarpanı 0 veya daha büyük olmalı' });
      return;
    }
    setConfirmBalance(true);
  };
  const doEdit = async () => {
    const identityIsBeingSaved = form.kyc_verified && (
      !user?.kyc_verified || form.tc_no || form.birth_date
    );
    if (identityIsBeingSaved && (
      !form.first_name.trim()
      || !form.last_name.trim()
      || !form.tc_no.trim()
      || !form.birth_date
    )) {
      toast({
        title: 'Kimlik bilgileri eksik',
        description: 'KYC doğrulaması için ad, soyad, T.C. kimlik no ve doğum tarihi zorunludur.',
      });
      return;
    }
    setBusy(true);
    const editPayload = { ...form };
    // Kimlik alanı bulunmayan eski, doğrulanmış hesapların e-posta/telefon
    // düzenlemesini bozmayız. Alanlardan biri girildiğinde ikisi de backend'de
    // gerçek KYC doğrulamasına tabi olur.
    if (user?.kyc_verified && !form.tc_no && !form.birth_date) {
      delete editPayload.tc_no;
      delete editPayload.birth_date;
    }
    // 2FA alanı yalnızca **kapatma** yönünde gönderilir: uç açma isteğini
    // 400 ile geri çevirir (yönetici üyenin kanal sahipliğini kanıtlayamaz),
    // yani kutusuna dokunulmamış bir kaydı olduğu gibi yollamak sıradan bir
    // e-posta düzenlemesini hataya düşürürdü.
    const twoFactorDisabled = !!user?.two_factor_enabled && !form.two_factor_enabled;
    if (!twoFactorDisabled) delete editPayload.two_factor_enabled;
    const payload = user?.is_admin
      ? Object.fromEntries(Object.entries(editPayload).filter(([key]) => key !== 'is_banned'))
      : editPayload;
    const ok = await onAction(user, 'edit', payload);
    if (ok && twoFactorDisabled) {
      toast({
        title: 'İki adımlı doğrulama kapatıldı',
        description: `${user.username} artık kod istenmeden giriş yapar; `
          + 'açık oturumları kapatıldı ve Authenticator kaydı silindi. '
          + 'Yeniden açmayı üye kendi Hesap Güvenliği sayfasından yapar.',
      });
    }
    setBusy(false);
  };
  const doCreate = async () => {
    if (!newUser.username || !newUser.password) { toast({ title: 'Kullanıcı adı ve şifre gerekli' }); return; }
    setBusy(true);
    try { await client.post('/admin/users', newUser); toast({ title: 'Kullanıcı eklendi' }); onCreated(); }
    catch (e) { toast({ title: 'Hata', description: e.response?.data?.detail || 'Başarısız' }); }
    finally { setBusy(false); }
  };

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
      <div className="relative admin-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-white/5 flex items-center gap-2">
          <h3 className="font-bold">
            {type === 'edit' && `Kullanıcı Düzenle: ${user.username}`}
            {type === 'balance' && `Bakiye ${sign > 0 ? 'Ekle' : 'Düş'}: ${user.username}`}
            {type === 'password' && `Şifre Sıfırla: ${user.username}`}
            {type === 'commission' && `Affiliate Komisyonu: ${user.username}`}
            {type === 'create' && 'Yeni Kullanıcı Ekle'}
          </h3>
          <button onClick={onClose} className="icon-btn view ml-auto"><X size={14} /></button>
        </div>
        <div className="p-5">
          {type === 'balance' && (
            <div className="space-y-3">
              <div className="text-sm text-slate-400">Mevcut Bakiye: <span className="font-bold text-white">{formatMoney(user.balance)}</span></div>
              <div><label className="text-xs text-slate-400 block mb-1">Tutar (₺)</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="admin-input" /></div>
              {sign > 0 && (
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Çevrim Çarpanı (x)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={wagerMultiplier}
                    onChange={(e) => setWagerMultiplier(e.target.value)}
                    className="admin-input"
                    placeholder="Örn. 30 — 0 = çevrimsiz"
                  />
                  <div className="text-xs mt-1 text-slate-500">
                    {wagerRequired > 0 ? (
                      <span className="text-amber-400">
                        Tutar bonus bakiyeye kilitlenir; {formatMoney(wagerRequired)} çevrim
                        tamamlanmadan ana bakiyeye geçmez ve çekilemez.
                      </span>
                    ) : (
                      'Çevrimsiz: tutar doğrudan ana bakiyeye eklenir, anında çekilebilir.'
                    )}
                  </div>
                </div>
              )}
              <div><label className="text-xs text-slate-400 block mb-1">Açıklama (opsiyonel)</label><input value={note} onChange={(e) => setNote(e.target.value)} className="admin-input" placeholder="Bonus / Manuel düzeltme..." /></div>
              <button disabled={busy} onClick={doBalance} className="admin-btn-primary w-full py-3">{busy && <Loader2 size={14} className="animate-spin inline mr-2" />}{sign > 0 ? 'Ekle' : 'Düş'}</button>
            </div>
          )}
          {type === 'edit' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400 block mb-1">Ad</label><input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Soyad</label><input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="admin-input" /></div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">T.C. Kimlik No</label>
                <input
                  value={form.tc_no}
                  onChange={(e) => setForm({
                    ...form,
                    tc_no: e.target.value.replace(/\D/g, '').slice(0, 11),
                  })}
                  className="admin-input"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="11 haneli"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Doğum Tarihi</label>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                  className="admin-input"
                />
              </div>
              <div><label className="text-xs text-slate-400 block mb-1">E-Posta</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Telefon</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="admin-input" /></div>
              <p className="sm:col-span-2 text-xs leading-5 text-slate-500">
                KYC kutusu açık kaydedildiğinde kimlik bilgileri doğrulanır ve
                KYC Onayları ekranına otomatik onaylı kayıt olarak işlenir.
              </p>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.kyc_verified} onChange={(e) => setForm({ ...form, kyc_verified: e.target.checked })} className="accent-cyan-500" /> KYC Doğrulandı</label>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-cyan-500" /> Aktif</label>
              {!user.is_admin && (
                <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_banned} onChange={(e) => setForm({ ...form, is_banned: e.target.checked })} className="accent-red-500" /> Yasaklı</label>
              )}
              <div className="sm:col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <BadgeCheck size={14} className={user.two_factor_enabled ? 'text-emerald-400' : 'text-slate-500'} />
                  İki Adımlı Doğrulama
                  <span className={`ml-auto ${user.two_factor_enabled ? 'badge-pill badge-green' : 'text-xs text-slate-500'}`}>
                    {user.two_factor_enabled ? twoFactorChannelLabel(user.two_factor_channel) : 'Kapalı'}
                  </span>
                </div>
                {user.two_factor_enabled ? (
                  <>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!form.two_factor_enabled}
                        onChange={(e) => setForm({ ...form, two_factor_enabled: !e.target.checked })}
                        className="accent-red-500"
                      />
                      İki adımlı doğrulamayı kapat
                    </label>
                    <p className="text-xs leading-5 text-slate-500">
                      {form.two_factor_enabled ? (
                        <>
                          Üye her girişte kod giriyor
                          {user.two_factor_source === 'registration' && ' (kayıt akışında açıldı)'}.
                          Telefonunu veya Authenticator uygulamasını kaybettiyse kutuyu işaretleyip kaydedin.
                        </>
                      ) : (
                        <span className="text-amber-400">
                          Kaydedildiğinde üyenin açık oturumları kapanır, Authenticator kaydı
                          silinir ve giriş yalnızca şifreyle yapılır. Yeniden açmayı üye kendi
                          Hesap Güvenliği sayfasından yapar.
                        </span>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-xs leading-5 text-slate-500">
                    Bu katman yönetici tarafından açılamaz: kanal sahipliğini (telefon, e-posta
                    ya da Authenticator) üyenin kendisi doğrulamalıdır.
                  </p>
                )}
              </div>
              <button disabled={busy} onClick={doEdit} className="admin-btn-primary sm:col-span-2 py-3">{busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet</button>
            </div>
          )}
          {type === 'password' && (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-400/25 bg-amber-400/5 p-3 text-xs text-slate-300">
                Yeni şifre anında geçerli olur, açık oturumlar kapatılır ve kullanıcıya bildirilmez.
                Şifreyi ilgili üyeye güvenli bir kanaldan siz iletin.
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Yeni Şifre (en az 8 karakter)</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password.value}
                  onChange={(e) => setPassword({ ...password, value: e.target.value })}
                  className="admin-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Yeni Şifre (tekrar)</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password.repeat}
                  onChange={(e) => setPassword({ ...password, repeat: e.target.value })}
                  className="admin-input"
                />
              </div>
              <button disabled={busy} onClick={doPassword} className="admin-btn-primary w-full py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Şifreyi Sıfırla
              </button>
            </div>
          )}
          {type === 'commission' && (
            commissionLoading || !commission ? (
              <div className="py-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
            ) : !commission.affiliate ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-white/10 bg-slate-900/50 p-3 text-sm text-slate-300">
                  Bu üyenin affiliate ortaklığı yok. Komisyon oranı tanımlamak için
                  önce ortak hesabı oluşturun; referans kodu otomatik üretilir ve
                  affiliate yetkisi verilir.
                </div>
                <button disabled={busy} onClick={createAffiliate} className="admin-btn-primary w-full py-3">
                  {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Ortak Yap ve Kod Üret
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-slate-900/50 px-3 py-2 text-sm">
                  <span className="text-slate-400 text-xs">Referans kodu</span>
                  <span className="font-mono text-cyan-400">{commission.affiliate.referral_code}</span>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Komisyon Modeli</label>
                  <select
                    value={commission.commission_type}
                    onChange={(e) => setCommission({ ...commission, commission_type: e.target.value })}
                    className="admin-input"
                  >
                    <option value="revshare">Gelir Payı — yönlendirilen üyenin net kaybından %</option>
                    <option value="deposit">Yatırım Yüzdesi — her yatırımdan %</option>
                    <option value="cpa">CPA — ilk yatırımı yapan üye başına sabit tutar</option>
                  </select>
                </div>
                {commission.commission_type === 'revshare' && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Gelir Payı Oranı (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={commission.revshare_percent}
                           onChange={(e) => setCommission({ ...commission, revshare_percent: e.target.value })}
                           className="admin-input" placeholder="Örn. 25" />
                  </div>
                )}
                {commission.commission_type === 'deposit' && (
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Yatırım Yüzdesi (%)</label>
                    <input type="number" step="0.1" min="0" max="100" value={commission.deposit_percent}
                           onChange={(e) => setCommission({ ...commission, deposit_percent: e.target.value })}
                           className="admin-input" placeholder="Örn. 5" />
                  </div>
                )}
                {commission.commission_type === 'cpa' && (
                  <>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">CPA Tutarı (₺)</label>
                      <input type="number" step="1" min="0" value={commission.cpa_amount}
                             onChange={(e) => setCommission({ ...commission, cpa_amount: e.target.value })}
                             className="admin-input" placeholder="Örn. 250" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 block mb-1">CPA için Min. İlk Yatırım (₺)</label>
                      <input type="number" step="1" min="0" value={commission.cpa_min_deposit}
                             onChange={(e) => setCommission({ ...commission, cpa_min_deposit: e.target.value })}
                             className="admin-input" placeholder="Örn. 500" />
                    </div>
                  </>
                )}
                <div className="rounded-lg border border-cyan-400/25 bg-cyan-400/5 p-3 text-[11px] text-slate-400">
                  Oran yalnızca bu ortak için geçerlidir ve manuel girilir.
                  Değer 0 bırakılırsa bu ortağa komisyon işlenmez.
                </div>
                <button disabled={busy} onClick={saveCommission} className="admin-btn-primary w-full py-3">
                  {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet
                </button>
              </div>
            )
          )}
          {type === 'create' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><label className="text-xs text-slate-400 block mb-1">Kullanıcı Adı *</label><input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} className="admin-input" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-slate-400 block mb-1">Şifre *</label><input type="password" autoComplete="new-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Ad</label><input value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Soyad</label><input value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">E-Posta</label><input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="admin-input" /></div>
              <div><label className="text-xs text-slate-400 block mb-1">Telefon</label><input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} className="admin-input" /></div>
              <div className="sm:col-span-2"><label className="text-xs text-slate-400 block mb-1">Başlangıç Bakiyesi</label><input type="number" min="0" step="0.01" value={newUser.balance} onChange={(e) => setNewUser({ ...newUser, balance: Number(e.target.value) })} className="admin-input" /></div>
              <button disabled={busy} onClick={doCreate} className="admin-btn-primary sm:col-span-2 py-3">{busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kullanıcı Oluştur</button>
            </div>
          )}
        </div>
      </div>
    </div>
    <ConfirmDialog
      open={confirmBalance}
      title={wagerRequired > 0 ? 'Çevrim şartlı bonus ver' : `Bakiyeyi ${sign > 0 ? 'artır' : 'azalt'}`}
      description={wagerRequired > 0
        ? `${user?.username} kullanıcısına ${formatMoney(Number(amount))} bonus bakiye `
          + `kilitli olarak yüklenecek. ${formatMoney(wagerRequired)} çevrim `
          + `(x${wagerFactor}) tamamlanmadan ana bakiyeye geçmez ve çekim talebi `
          + 'reddedilir. İşlem geri alınabilir bir finans kaydı ve audit logu oluşturur.'
        : `${user?.username} kullanıcısının bakiyesi ${formatMoney(Number(amount))} ${sign > 0 ? 'artırılacak' : 'azaltılacak'}. İşlem geri alınabilir bir finans kaydı ve audit logu oluşturur.`}
      confirmLabel={wagerRequired > 0 ? 'Bonusu yükle' : (sign > 0 ? 'Bakiyeyi artır' : 'Bakiyeyi azalt')}
      tone={sign > 0 ? 'warning' : 'danger'}
      busy={busy}
      onCancel={() => !busy && setConfirmBalance(false)}
      onConfirm={applyBalance}
    />
    </>
  );
};

export default UserModal;
